import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { userIdSelector } from '@/domain/env/envSelector';
import {
  gridPresetClearedAction,
  gridPresetDefaultColumnsChangedAction,
  gridPresetResolvedAction,
} from '@/domain/documents/gridPreset/gridPresetActions';
import { gridPresetCompanyIdSelector } from '@/domain/documents/gridPreset/gridPresetSelectors';
import { readStoredPreset } from '@/domain/documents/gridPreset/persistence';
import { resolveGridPreset } from '@/domain/documents/gridPreset/resolve';
import type { SavedColumnLayout } from '@/domain/documents/savedColumnLayout';
import { useGridPresets, useSharedGridPreset } from '@/domain/documents/queries/gridPresets';
import { gridPresetsList } from '@/domain/documents/queries/gridPresets.select';
import { browserStorage } from '@/lib/storage';

export const SHARED_PRESET_PARAM = 'sharedPreset';

// The address without `?sharedPreset=`: the grid's refresh-on-search effect compares this, so removing the parameter
// once the shared preset is applied sends no request.
export const searchWithoutSharedPreset = (search: string) => {
  const params = new URLSearchParams(search);
  params.delete(SHARED_PRESET_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : '';
};

// Puts the workspace grid's layout into the `gridPreset` slice, once per company, and again for every shared link.
// Waits for the presets list, the shared preset (from the address, or the stored one after a reload) and the default
// columns, then dispatches one `layoutResolved` with the stored choice. The grid renders only once the slice belongs
// to the route's company. A presets list that failed to load resolves to Default without overwriting the stored
// choice.
export const useWorkspaceGridPreset = (defaultColumns: SavedColumnLayout) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();
  const { companyId = '' } = useParams();
  const userId = useSelector(userIdSelector) as string | number | null;
  const layoutCompanyId = useSelector(gridPresetCompanyIdSelector);

  const sharedId = new URLSearchParams(search).get(SHARED_PRESET_PARAM);
  const isResolved = layoutCompanyId === companyId;
  const hasDefaultColumns = defaultColumns.length > 0;
  // Read when the company or user changes; only used until the company is resolved.
  const storedChoice = useMemo(
    () => (userId && companyId ? readStoredPreset(browserStorage(), String(userId), companyId) : null),
    [userId, companyId],
  );
  // A shared preset that was current when the page was left: fetched again by id, it is not in the user's list.
  const storedSharedId =
    !sharedId && !isResolved && storedChoice?.sharedPresetId && storedChoice.sharedPresetId === storedChoice.presetId
      ? storedChoice.sharedPresetId
      : null;
  const sharedQueryId = sharedId ?? storedSharedId;

  const presets = useGridPresets(companyId, { select: gridPresetsList });
  const shared = useSharedGridPreset(companyId, sharedQueryId);
  // The shared link already applied: the replace navigation that drops it lands a render later.
  const appliedSharedId = useRef<string | null>(null);

  // Another company's layout must not show — in the grid, the chips or the empty state — while this one resolves.
  useEffect(() => {
    if (layoutCompanyId && layoutCompanyId !== companyId) dispatch(gridPresetClearedAction());
  }, [dispatch, layoutCompanyId, companyId]);

  // v4: a failed query is no longer loading.
  const presetsSettled = !presets.isLoading;
  const sharedSettled = !sharedQueryId || !shared.isLoading;
  const sharedPreset = sharedQueryId ? (shared.data?.result ?? null) : null;
  // A failed load is not a deleted preset: resolving without it shows Default but must not store it over the choice.
  const loadFailed = presets.isError || (Boolean(sharedQueryId) && shared.isError);

  useEffect(() => {
    // Once the parameter is gone, the same link opened again is a new request.
    if (!sharedId) appliedSharedId.current = null;
    if (!companyId || !hasDefaultColumns || !presetsSettled || !sharedSettled) return;
    if (isResolved && (!sharedId || appliedSharedId.current === sharedId)) return;
    if (!isResolved && !userId) return;

    appliedSharedId.current = sharedId;
    if (!isResolved || sharedPreset) {
      dispatch(
        gridPresetResolvedAction({
          ...resolveGridPreset({
            companyId,
            presets: presets.data ?? [],
            shared: sharedPreset,
            defaultColumns,
            // A link applies the shared preset as saved.
            stored: sharedId ? null : storedChoice,
          }),
          ...(loadFailed ? { persist: false } : {}),
        }),
      );
    }

    if (sharedId) {
      navigate({ pathname, search: searchWithoutSharedPreset(search), hash }, { replace: true });
    }
  }, [
    dispatch,
    navigate,
    companyId,
    hasDefaultColumns,
    defaultColumns,
    presetsSettled,
    sharedSettled,
    isResolved,
    userId,
    storedChoice,
    sharedId,
    sharedPreset,
    presets.data,
    loadFailed,
    pathname,
    search,
    hash,
  ]);

  // Default's columns follow the column definitions (the slice ignores this when another preset is current).
  useEffect(() => {
    if (isResolved && hasDefaultColumns) dispatch(gridPresetDefaultColumnsChangedAction(defaultColumns));
  }, [dispatch, isResolved, hasDefaultColumns, defaultColumns]);

  return { isResolved };
};
